import { TTypeOption } from 'legacy/app/components/dropdowns/types/TTypeOption';
import UnitsOfMeasureDropdown from 'legacy/app/components/dropdowns/UnitsOfMeasuresDropdown';
import { FormControl } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import Budget from './Budget';
import Deposit from './Deposit';
import LocationSelect from './LocationSelect';
import PurchaseComponentsTable from './PurchaseComponentsTable';
import QuantityInput from './QuantityInput';
import SalesCategoriesSelect from './SalesCategoriesSelect';
import Label from 'legacy/app/components/label/Label';
import { ItemFormValues } from '../types/FormValues';
import { Item } from 'legacy/lib/api/types/Item';
import { WhatChanged } from 'legacy/lib/api/types/WhatChangedItems';
import ImageGallery from 'legacy/app/components/ImageGallery/ImageGallery';
import LocationsQuickAddModal from 'legacy/app/components/modal/LocationsQuickAddModal';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TTypeLocation } from 'legacy/lib/api/types/Location';
import { useParams } from 'react-router-dom';
import TextEditor from 'legacy/app/components/textEditor/TextEditor';
import { useFeature } from 'use-feature';
import { FeatureFlags } from 'legacy/app/enums/featureFlags/featureFlags';
import { ItemGroupSelect } from './ItemGroupSelect';
import classNames from 'classnames';

type TItemInfoProps = {
	projectCode: string;
	submitFormFromOutside: (
		customRedirectPath?: string,
		getEditedItem?: (item: Item) => void
	) => Promise<void>;
	disableAddComponentButton: boolean;
	disableButtons: boolean;
	isItemFromInventory: boolean;
	itemToEdit?: Item;
	handleRecalculate: (whatPropertyChanged: `${WhatChanged}`) => void;
	setDelayReload: (value: boolean) => void;
	hasSalesCategory: boolean;
	formValues?: ItemFormValues;
};

const Info = ({
	itemToEdit,
	projectCode,
	isItemFromInventory,
	handleRecalculate,
	disableAddComponentButton,
	disableButtons,
	submitFormFromOutside,
	setDelayReload,
	hasSalesCategory,
	formValues,
}: TItemInfoProps) => {
	const [createOptionLocationName, setCreateOptionLocationName] = useState('');
	const methods = useFormContext<ItemFormValues>();
	const { setValue, watch } = methods;
	const nameInput = methods.register('itemName');
	const queryClient = useQueryClient();
	const { id: projectId } = useParams();
	const isItemGroupsEnabled = useFeature(FeatureFlags.ItemGroups);
	const [createGroupName, setCreateGroupName] = useState<string | null>(null);

	useEffect(() => {
		return () => {
			setValue('initialDescription', watch('description').html);
		};
	}, [setValue, watch]);

	return (
		<>
			<div
				className={classNames('xl:tw-flex tw-!gap-4 tw-p-10 tw-flex-wrap', {
					'!tw-py-5': isItemGroupsEnabled,
				})}
			>
				<ImageGallery />
				<div className="tw-flex tw-flex-col xl:tw-grid tw-items-center lg:tw-flex-1 tw-gap-x-6 tw-gap-y-4 tw-grid-cols-3">
					<Label
						label="Name"
						className="tw-mt-[1rem] xl:tw-mt-[0] tw-text-right"
					/>
					<FormControl
						type="text"
						className="tw-h-8 tw-col-span-2"
						placeholder="Name"
						id="Name"
						data-testid="items-page-name-input"
						maxLength={45}
						name={nameInput.name}
						onChange={nameInput.onChange}
						ref={nameInput.ref}
					/>
					<Label
						label="Description"
						className="tw-pt-4 tw-self-baseline tw-text-right"
					/>
					<Controller
						control={methods.control}
						name="description"
						render={({ field }) => (
							<div className="tw-col-span-2 tw-w-full">
								<TextEditor
									initialData={methods.watch('initialDescription')}
									onChange={(e) => field.onChange(e.target)}
								/>
							</div>
						)}
					/>
					<LocationSelect
						onCreateOption={(value) => setCreateOptionLocationName(value)}
						disabled={disableButtons}
						handleRecalculate={handleRecalculate}
					/>
					{isItemGroupsEnabled && (
						<ItemGroupSelect
							projectCode={projectCode}
							disabled={disableButtons}
							onChange={(option) => {
								if (option === null) {
									setCreateGroupName(null);
									methods.setValue('groupname', {
										value: '',
										label: '',
									});
								}
							}}
							onCreateOption={(value) => {
								setCreateGroupName(value);
								methods.setValue('groupname', {
									value: value,
									label: value,
								});
							}}
							newOption={createGroupName}
						/>
					)}

					<Label label="Quantity" className="tw-text-right" />
					<QuantityInput
						disabled={disableButtons}
						handleRecalculate={handleRecalculate}
					/>
					<div className="tw-flex tw-flex-col xl:tw-flex-row tw-justify-center xl:tw-justify-start tw-w-full lg:tw-col-span-1 tw-items-center !tw-gap-4 ">
						<Label label="Units of Measure" />
						<Controller
							control={methods.control}
							name="unitmeasure"
							render={({ field }) => (
								<div className="tw-flex-1 tw- lg:tw-min-w-28 tw-w-full">
									<UnitsOfMeasureDropdown
										inputId="items-page-unit-measure-input"
										value={field.value as TTypeOption}
										onChange={(val) => field.onChange(val)}
									/>
								</div>
							)}
						/>
					</div>
					<Budget
						disabled={disableButtons}
						handleRecalculate={handleRecalculate}
					/>
					<SalesCategoriesSelect
						disabled={disableButtons}
						handleRecalculate={handleRecalculate}
					/>
				</div>
			</div>
			<Deposit
				itemToEdit={itemToEdit}
				disableDepositSelect={disableButtons}
				handleRecalculate={handleRecalculate}
			/>
			<PurchaseComponentsTable
				isItemFromInventory={isItemFromInventory}
				disableAdd={disableAddComponentButton || !formValues?.scat?.value}
				disableButtons={disableButtons}
				submitMethods={submitFormFromOutside}
				setDelayReload={setDelayReload}
				hasSalesCategory={hasSalesCategory}
			/>
			{createOptionLocationName && (
				<LocationsQuickAddModal
					projCode={projectCode}
					locAddName={createOptionLocationName}
					hideModal={() => setCreateOptionLocationName('')}
					handleSuccess={(res: TTypeLocation) => {
						methods.setValue('loc', {
							value: res.loc,
							label: '',
						});
						// Need to wait one second because the location summaries endpoint takes a bit to update
						setTimeout(async () => {
							await queryClient.resetQueries([
								'dropdowns',
								'locationsSummaries',
								projectId,
							]);
							setCreateOptionLocationName('');
							handleRecalculate(WhatChanged.LOCATION);
						}, 1000);
					}}
				/>
			)}
		</>
	);
};
Info.displayName = 'ItemsInfoTab';
export default Info;
